import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createCase } from "../services/api";

function NewCase({ onLogout }) {
  const [form, setForm] = useState({
    type: "",
    url: "",
    uploaderInfo: "" // Additional field for NCC cases
  });
  const [submissionState, setSubmissionState] = useState({
    isGenerating: false,
    isReadyToSend: false,
    isSending: false,
    isComplete: false,
    generatedContent: '',
    caseNumber: null,
    emailRecipient: null,
    emailSentAt: null,
    error: null
  });
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleGenerateContent = async (e) => {
    e.preventDefault();
    
    setSubmissionState({
      isGenerating: true,
      isReadyToSend: false,
      isSending: false,
      isComplete: false,
      generatedContent: '',
      error: null
    });

    try {
      // Use environment variable for API base URL
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      // Only generate DMCA content, don't save to DB yet
      const response = await fetch(`${API_BASE_URL}/cases/generate-dmca`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        throw new Error('Failed to generate DMCA content');
      }

      const data = await response.json();
      
      setSubmissionState(prev => ({
        ...prev,
        isGenerating: false,
        isReadyToSend: true,
        generatedContent: data.dmcaText
      }));

    } catch (err) {
      console.error(err);
      setSubmissionState(prev => ({
        ...prev,
        isGenerating: false,
        error: err.message || "Error generating DMCA content"
      }));
    }
  };

  const handleSendAndSave = async () => {
    setSubmissionState(prev => ({
      ...prev,
      isSending: true,
      error: null
    }));

    try {
      // Now save to DB and send email
      const response = await createCase({
        ...form,
        dmcaText: submissionState.generatedContent
      });
      
      setSubmissionState(prev => ({
        ...prev,
        isSending: false,
        isComplete: true,
        caseNumber: response.caseNumber,
        emailRecipient: response.emailRecipient,
        emailSentAt: response.emailSentAt
      }));

      // Reset form after success
      setTimeout(() => {
        setForm({ type: "", url: "" });
        setSubmissionState({
          isGenerating: false,
          isReadyToSend: false,
          isSending: false,
          isComplete: false,
          generatedContent: '',
          caseNumber: null,
          emailRecipient: null,
          emailSentAt: null,
          error: null
        });
      }, 5000);

    } catch (err) {
      console.error(err);
      setSubmissionState(prev => ({
        ...prev,
        isSending: false,
        error: err.message || "Error submitting case"
      }));
    }
  };

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/login");
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: "white", 
        padding: "1rem 2rem", 
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        borderBottom: "1px solid #e9ecef"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1 style={{ margin: 0, fontSize: "1.5rem", color: "#333" }}>
            Digital Safe India - Report Case
          </h1>
          <div style={{ display: "flex", gap: "1rem" }}>
            <a 
              href="/tools" 
              style={{
                backgroundColor: "#17a2b8",
                color: "white",
                textDecoration: "none",
                padding: "0.5rem 1rem",
                borderRadius: "4px"
              }}
            >
              🏠 Tools Dashboard
            </a>
            <a 
              href="/dashboard" 
              style={{
                backgroundColor: "#6c757d",
                color: "white",
                textDecoration: "none",
                padding: "0.5rem 1rem",
                borderRadius: "4px"
              }}
            >
              📊 DMCA Dashboard
            </a>
            <button
              onClick={handleLogout}
              style={{
                backgroundColor: "#dc3545",
                color: "white",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: "2rem", display: "flex", justifyContent: "center" }}>
        <div style={{ 
          backgroundColor: "white", 
          padding: "2rem", 
          borderRadius: "8px", 
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)", 
          width: "100%", 
          maxWidth: "500px" 
        }}>
          <h2 style={{ textAlign: "center", marginBottom: "1.5rem", color: "#333" }}>Submit New Case</h2>
          <div>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              required
              style={{ 
                width: "100%", 
                padding: "0.75rem", 
                border: "1px solid #ddd", 
                borderRadius: "4px",
                fontSize: "1rem",
                marginBottom: "1rem"
              }}
            >
              <option value="">Select Case Type</option>
              <option value="malware">Malware</option>
              <option value="phishing">Phishing</option>
              <option value="ncc">NCC (Non-Consensual Content)</option>
              <option value="spy-app">Spy App</option>
            </select>
            
            {/* Help text for case types */}
            {form.type && (
              <div style={{ 
                backgroundColor: "#e7f3ff", 
                border: "1px solid #b3d9ff",
                borderRadius: "4px",
                padding: "0.75rem",
                marginBottom: "1rem",
                fontSize: "0.9rem",
                color: "#0066cc"
              }}>
                {form.type === "ncc" && (
                  <>
                    <strong>NCC (Non-Consensual Content):</strong> Use this for intimate images or videos shared without consent. 
                    This will generate a specialized removal request focusing on privacy violations and platform safety policies.
                  </>
                )}
                {form.type === "malware" && (
                  <>
                    <strong>Malware:</strong> Use this for malicious software, viruses, or harmful downloads that pose security threats.
                  </>
                )}
                {form.type === "phishing" && (
                  <>
                    <strong>Phishing:</strong> Use this for fraudulent websites or content designed to steal personal information.
                  </>
                )}
                {form.type === "spy-app" && (
                  <>
                    <strong>Spy App:</strong> Use this for surveillance software or apps that violate privacy without proper consent.
                  </>
                )}
              </div>
            )}
            <input
              type="url"
              name="url"
              value={form.url}
              onChange={handleChange}
              placeholder="Enter URL to report"
              required
              style={{ 
                width: "100%", 
                padding: "0.75rem", 
                border: "1px solid #ddd", 
                borderRadius: "4px",
                fontSize: "1rem",
                marginBottom: "1rem"
              }}
            />
            
            {/* Additional field for NCC cases */}
            {form.type === "ncc" && (
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ 
                  display: "block", 
                  marginBottom: "0.5rem", 
                  color: "#555",
                  fontSize: "0.9rem",
                  fontWeight: "500"
                }}>
                  Username/Handle of uploader (if known):
                </label>
                <input
                  type="text"
                  name="uploaderInfo"
                  value={form.uploaderInfo}
                  onChange={handleChange}
                  placeholder="Enter username or handle (optional)"
                  style={{ 
                    width: "100%", 
                    padding: "0.75rem", 
                    border: "1px solid #ddd", 
                    borderRadius: "4px",
                    fontSize: "1rem"
                  }}
                />
                <small style={{ 
                  color: "#666", 
                  fontSize: "0.8rem",
                  display: "block",
                  marginTop: "0.25rem"
                }}>
                  This information helps platforms identify and take action against the responsible account.
                </small>
              </div>
            )}
            {!submissionState.isReadyToSend ? (
              <button
                type="button"
                onClick={handleGenerateContent}
                disabled={submissionState.isGenerating}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  backgroundColor: submissionState.isGenerating ? "#6c757d" : "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "1rem",
                  cursor: submissionState.isGenerating ? "not-allowed" : "pointer",
                  opacity: submissionState.isGenerating ? 0.7 : 1
                }}
              >
                {submissionState.isGenerating ? "Generating..." : "Generate DMCA Content"}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSendAndSave}
                disabled={submissionState.isSending}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  backgroundColor: submissionState.isSending ? "#6c757d" : "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "1rem",
                  cursor: submissionState.isSending ? "not-allowed" : "pointer",
                  opacity: submissionState.isSending ? 0.7 : 1
                }}
              >
                {submissionState.isSending ? "Sending..." : "Send Email & Submit Case"}
              </button>
            )}
          </div>

          {/* Submission Progress and Content Preview */}
          {(submissionState.isGenerating || submissionState.isReadyToSend || submissionState.isSending || submissionState.isComplete) && (
            <div style={{ 
              marginTop: "2rem", 
              padding: "1.5rem", 
              backgroundColor: "#f8f9fa", 
              borderRadius: "8px",
              border: "1px solid #e9ecef"
            }}>
              {/* Progress Steps */}
              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#333" }}>Progress:</span>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <div style={{ 
                    flex: 1, 
                    height: "4px", 
                    backgroundColor: (submissionState.isGenerating || submissionState.isReadyToSend || submissionState.isSending || submissionState.isComplete) ? "#007bff" : "#e9ecef",
                    borderRadius: "2px",
                    transition: "background-color 0.3s"
                  }}></div>
                  <div style={{ 
                    flex: 1, 
                    height: "4px", 
                    backgroundColor: (submissionState.isReadyToSend || submissionState.isSending || submissionState.isComplete) ? "#007bff" : "#e9ecef",
                    borderRadius: "2px",
                    transition: "background-color 0.3s"
                  }}></div>
                  <div style={{ 
                    flex: 1, 
                    height: "4px", 
                    backgroundColor: (submissionState.isSending || submissionState.isComplete) ? "#007bff" : "#e9ecef",
                    borderRadius: "2px",
                    transition: "background-color 0.3s"
                  }}></div>
                  <div style={{ 
                    flex: 1, 
                    height: "4px", 
                    backgroundColor: submissionState.isComplete ? "#28a745" : "#e9ecef",
                    borderRadius: "2px",
                    transition: "background-color 0.3s"
                  }}></div>
                </div>
              </div>

              {/* Current Step Status */}
              {submissionState.isGenerating && (
                <div style={{ textAlign: "center", color: "#007bff" }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>🤖</div>
                  <h4 style={{ margin: "0 0 0.5rem 0", color: "#333" }}>Generating DMCA Content</h4>
                  <p style={{ margin: 0, color: "#666" }}>AI is creating a professional takedown notice...</p>
                  <div style={{ 
                    display: "inline-block", 
                    marginTop: "1rem",
                    width: "20px", 
                    height: "20px", 
                    border: "2px solid #007bff", 
                    borderTop: "2px solid transparent", 
                    borderRadius: "50%", 
                    animation: "spin 1s linear infinite" 
                  }}></div>
                </div>
              )}

              {submissionState.isReadyToSend && submissionState.generatedContent && !submissionState.isSending && (
                <div>
                  <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                    <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>📄</div>
                    <h4 style={{ margin: "0 0 0.5rem 0", color: "#333" }}>AI-Generated DMCA Notice</h4>
                    <p style={{ margin: 0, color: "#666" }}>Review the content that will be sent to authorities</p>
                  </div>
                  
                  {/* Email Preview Card */}
                  <div style={{ 
                    backgroundColor: "white", 
                    border: "1px solid #ddd", 
                    borderRadius: "6px", 
                    overflow: "hidden",
                    marginBottom: "1rem"
                  }}>
                    {/* Email Header */}
                    <div style={{ 
                      backgroundColor: "#f8f9fa", 
                      padding: "1rem", 
                      borderBottom: "1px solid #ddd",
                      fontSize: "0.9rem"
                    }}>
                      <div style={{ marginBottom: "0.25rem" }}>
                        <strong>To:</strong> amitpnd555@gmail.com
                      </div>
                      <div style={{ marginBottom: "0.25rem" }}>
                        <strong>Subject:</strong> DMCA Takedown Request - {form.type.charAt(0).toUpperCase() + form.type.slice(1)}
                      </div>
                      <div>
                        <strong>From:</strong> Digital Safe India &lt;helpvictims9@gmail.com&gt;
                      </div>
                    </div>
                    
                    {/* Email Content */}
                    <div style={{ 
                      padding: "1rem", 
                      maxHeight: "300px", 
                      overflowY: "auto",
                      fontSize: "0.9rem",
                      lineHeight: "1.5",
                      whiteSpace: "pre-wrap"
                    }}>
                      {submissionState.generatedContent}
                    </div>
                  </div>

                  {/* Copy Button */}
                  <div style={{ textAlign: "center" }}>
                    <button
                      onClick={() => navigator.clipboard.writeText(submissionState.generatedContent)}
                      style={{
                        backgroundColor: "#6c757d",
                        color: "white",
                        border: "none",
                        padding: "0.5rem 1rem",
                        borderRadius: "4px",
                        fontSize: "0.9rem",
                        cursor: "pointer"
                      }}
                    >
                      📋 Copy Content
                    </button>
                  </div>
                </div>
              )}

              {submissionState.isSending && (
                <div style={{ textAlign: "center", color: "#17a2b8" }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>📧</div>
                  <h4 style={{ margin: "0 0 0.5rem 0", color: "#333" }}>Sending Email</h4>
                  <p style={{ margin: 0, color: "#666" }}>Delivering DMCA notice to authorities...</p>
                  <div style={{ 
                    display: "inline-block", 
                    marginTop: "1rem",
                    width: "20px", 
                    height: "20px", 
                    border: "2px solid #17a2b8", 
                    borderTop: "2px solid transparent", 
                    borderRadius: "50%", 
                    animation: "spin 1s linear infinite" 
                  }}></div>
                </div>
              )}

              {submissionState.isComplete && (
                <div style={{ textAlign: "center", color: "#28a745" }}>
                  <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>✅</div>
                  <h4 style={{ margin: "0 0 0.5rem 0", color: "#28a745" }}>Case Submitted Successfully!</h4>
                  {submissionState.caseNumber && (
                    <div style={{ 
                      backgroundColor: "#d4edda", 
                      border: "1px solid #c3e6cb", 
                      borderRadius: "6px", 
                      padding: "1rem", 
                      margin: "1rem 0",
                      textAlign: "left"
                    }}>
                      <p style={{ margin: "0 0 0.5rem 0", fontWeight: "bold", color: "#155724" }}>
                        📋 Case Number: {submissionState.caseNumber}
                      </p>
                      <p style={{ margin: "0 0 0.5rem 0", color: "#155724" }}>
                        📧 Email sent to: {submissionState.emailRecipient}
                      </p>
                      <p style={{ margin: 0, color: "#155724", fontSize: "0.9rem" }}>
                        🕒 Sent at: {new Date(submissionState.emailSentAt).toLocaleString()}
                      </p>
                      <div style={{ marginTop: "1rem", textAlign: "center" }}>
                        <a
                          href={`/case/${submissionState.caseNumber}`}
                          style={{
                            backgroundColor: "#007bff",
                            color: "white",
                            textDecoration: "none",
                            padding: "0.5rem 1rem",
                            borderRadius: "4px",
                            fontSize: "0.9rem",
                            display: "inline-block"
                          }}
                        >
                          📄 View Case Details
                        </a>
                      </div>
                    </div>
                  )}
                  <p style={{ margin: 0, color: "#666" }}>DMCA notice has been sent to the authorities</p>
                  <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.8rem", color: "#999" }}>
                    Form will reset automatically in a few seconds...
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {submissionState.error && (
            <div style={{ 
              marginTop: "1rem", 
              padding: "1rem", 
              backgroundColor: "#f8d7da", 
              color: "#721c24",
              borderRadius: "4px",
              border: "1px solid #f5c6cb"
            }}>
              <strong>Error:</strong> {submissionState.error}
            </div>
          )}
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default NewCase;
